import { useSupplierBriefingFeedback, useSupplierReportFeedback } from '../../api/supplier_dynamics';
import { Button, Card, Input, notification, Popover } from 'antd';
import { DislikeFilled, DislikeOutlined, LikeFilled, LikeOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { SupplierTabType } from '../../sb/shared/constants';

interface FeedbackContentProps {
  id: number;
  supplierTabType: string;
}

export const FeedbackContent: React.FC<FeedbackContentProps> = ({ id, supplierTabType }) => {
  const [briefingFeedbackScore, setBriefingFeedbackScore] = useState<number>();
  const [reportFeedbackScore, setReportFeedbackScore] = useState<number>();
  const [open, setOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState<string>();

  useEffect(() => {
    setBriefingFeedbackScore(undefined);
    setReportFeedbackScore(undefined);
  }, [id]);

  const hide = () => {
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const successNotification = () => {
    notification.success({
      message: 'Feedback saved successfully',
      duration: 4
    });
  };

  const errorNotification = () => {
    notification.error({
      message: 'Failed to save feedback. Please try again',
      duration: 4
    });
  };

  const { mutate: insertSupplierBriefingFeedback } = useSupplierBriefingFeedback({
    onSuccess: async () => successNotification(),
    onError: () => errorNotification()
  });

  const { mutate: insertSupplierReportFeedback } = useSupplierReportFeedback({
    onSuccess: async () => successNotification(),
    onError: () => errorNotification()
  });

  /**
   * Handle feedback save.
   * @param {string} type - The type of the feedback. Can be "briefing" or "report".
   * @param {number} score - The score given for the feedback.
   * @param {string} [feedback] - Optional feedback text.
   * @returns {void} - No return value.
   */
  const handleFeedbackSave = (type: string, score: number, feedback?: string): void => {
    if (type === SupplierTabType.BRIEFING) {
      insertSupplierBriefingFeedback({ id: Number(id), score, feedback });
      setBriefingFeedbackScore(score);
    } else if (type === SupplierTabType.REPORT) {
      insertSupplierReportFeedback({ id: Number(id), score, feedback });
      setReportFeedbackScore(score);
    }
    score === -1 && hide();
    setFeedbackText(undefined);
  };

  /**
   * Renders a dislike comment box.
   * @param type
   * @returns {JSX.Element} The JSX element representing the dislike comment box.
   */
  const renderDislikeCommentBox = (type: string): React.ReactElement => {
    const disliked =
      (type === SupplierTabType.BRIEFING && briefingFeedbackScore === -1) ||
      (type === SupplierTabType.REPORT && reportFeedbackScore === -1);
    return (
      <Popover
        content={
          <>
            <Input.TextArea
              className="mb-3"
              rows={2}
              placeholder={'Add your comment/feedback'}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <Button
              className="text-right"
              type="primary"
              size="small"
              onClick={() => handleFeedbackSave(type, -1, feedbackText)}
            >
              Submit
            </Button>
          </>
        }
        title={<span className="font-bold">Please add a comment</span>}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
      >
        <Button
          type="text"
          icon={
            disliked ? (
              <DislikeFilled className="text-red-500 mr-1" />
            ) : (
              <DislikeOutlined className="cursor-pointer mr-1" />
            )
          }
        >
          No
        </Button>
      </Popover>
    );
  };

  /**
   * Render the feedback content based on the given type.
   * @param {string} type - The type of the feedback content.
   * @returns {React.ReactElement} - The rendered feedback content.
   */
  const renderFeedbackContent = (type: string): React.ReactElement => {
    const liked =
      (type === SupplierTabType.BRIEFING && briefingFeedbackScore === 1) ||
      (type === SupplierTabType.REPORT && reportFeedbackScore === 1);
    return (
      <Card className="w-52" bordered={false}>
        <span className="text-gray-500">Do you like this report?</span>
        <div className="flex">
          <Button
            type="text"
            icon={liked ? <LikeFilled className="text-blue-500" /> : <LikeOutlined className="cursor-pointer" />}
            onClick={() => handleFeedbackSave(type, 1)}
          >
            Yes
          </Button>
          {renderDislikeCommentBox(type)}
        </div>
      </Card>
    );
  };

  return renderFeedbackContent(supplierTabType);
};
